import { useTranslation } from 'next-i18next';

import { useMemo, useState } from 'react';

import { urlJoinP } from 'url-join-ts';

import { LoadingState } from '@nocowanie/core/enums';

import { createAxiosInstance } from '@app/common';
import { NewsletterUi } from '@app/components/organisms/newsletter/newsletter.types';
import { NewsletterFormModel } from '@app/components/organisms/newsletter/newsletter-form.model';
import { SalesManagoTag } from '@app/enums';
import { environment } from '@app/environments/environment';

export const useNewsletterApi = (tag: SalesManagoTag) => {
    const axios = createAxiosInstance();
    const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.IDLE);

    const onSubmit = async ({ email, name }: NewsletterFormModel): Promise<void> => {
        setLoadingState(LoadingState.PENDING);

        try {
            await axios
                .post(urlJoinP(environment.SSR_API_HOST, ['sm', 'newsletter', '/']), {
                    name,
                    email,
                    tag,
                })
                .then(res => {
                    const {
                        data: {
                            data: { success },
                        },
                    } = res;
                    if (success) {
                        setLoadingState(LoadingState.SUCCESS);
                    } else {
                        setLoadingState(LoadingState.ERROR);
                    }
                });
        } catch (error) {
            console.error('Error submitting newsletter:', error);
            setLoadingState(LoadingState.ERROR);
        }
    };

    return {
        loadingState,
        onSubmit,
    };
};

export const useNewsletterUi = (loadingState: LoadingState): NewsletterUi => {
    const { t } = useTranslation('home', { keyPrefix: 'newsletter' });

    return useMemo<NewsletterUi>(() => {
        const bgColors = {
            success: 'green.50',
            error: 'red.50',
            initial: 'secondary.50',
        };

        return {
            componentStyle:
                loadingState === LoadingState.SUCCESS
                    ? {
                          bg: bgColors.success,
                          mb: { base: 4, md: undefined },
                          alignSelf: 'end',
                      }
                    : loadingState === LoadingState.ERROR
                      ? {
                            bg: bgColors.error,
                            alignSelf: 'center',
                        }
                      : {
                            bg: bgColors.initial,
                            alignSelf: 'center',
                        },
            translation: t(loadingState === LoadingState.SUCCESS ? 'successStatus' : 'idleStatus', {
                returnObjects: true,
            }) as NewsletterUi['translation'],
        };
    }, [loadingState]);
};
